import { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import LaunchIcon from '@mui/icons-material/Launch';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

import {
  TextField,
  IconButton,
  InputAdornment,
  Avatar,
  Divider,
  MenuList,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography
} from '@mui/material';

import classes from './classes.module.scss';
import Modal from 'app/components/Modal';
import useDeeplink from 'app/hooks/useDeeplink';
import Loading from 'shared/components/Loading';

import {
  globalSearch
} from 'app/services/globalSearch';

import {
  MOMENT_IMAGE_URL
} from 'app/utils/Constants';

import {
  getLocalizedDate
} from 'shared/utils/Utils';

import {
  jobs,
  customers
} from 'app/resources';

import {
  OPERATOR_KEY,
  OR_OPERATOR,
  AND_OPERATOR,
} from 'app/hooks/useFilter';

const JOB_RESULT_TYPE = 0;
const CUSTOMER_RESULT_TYPE = 1;

const buildHeaderItem = props => {
  const {
    header,
    linkText,
    linkHref,
    onLinkClick
  } = props;

  let text = <p className={classes.headerTitle}>{header}</p>;
  let divider = true;

  let style = {
    height: 'auto',
    padding: '0.5rem 1rem'
  }

  let actionIcon = null;
  if (linkText) {
    actionIcon = (
      <span className={classes.headerLink}>
        <span onClick={onLinkClick}>
          {linkText}
        </span>
        <a title="Open new tab" href={linkHref} target="_blank">
          <IconButton>
            <LaunchIcon />
          </IconButton>
        </a>
      </span>
    )
  }

  return {
    text,
    divider,
    style,
    actionIcon
  };
}

const buildResultItem = props => {
  const {
    title,
    subTitle,
    imageUrl,
    //extraPanel,
    onClick,
    newTabLink,
    indented,
    height
  } = props;

  let text = <ItemText title={title} subTitle={subTitle} />;
  let icon = <ItemIcon imageUrl={imageUrl} />;

  let actionIcon = (
    <a href={newTabLink} target="_blank" onClick={e => e.stopPropagation()}>
      <IconButton title="Open new tab">
        <LaunchIcon />
      </IconButton>
    </a>
  )

  // indented job records are a submenu with a single item,
  // nested under each customer.
  // if indented then show a divider underneath
  let divider = indented;

  return {
    text,
    icon,
    actionIcon,
    onClick,
    indented,
    divider,
    height,
    //extraPanel
  }
}

const buildCustomerItem = props => {
  const {
    query,
    result,
    viewCustomer
  } = props;

  const {
    id,
    name,
    email,
    phone,
    access_code,
    cover_moment_id,
    created_at,
    photo_count,
    status_id
  } = result.customer;

  // set title to name, email, phone or access code based on availability.
  // set subTitle to the next available field in this order.
  // fallback to defaults if no field is set
  let titleFallback = `No Contact Info`;
  let subTitleFallback = ''; // blank for now

  let title, subTitle = '';
  if (name) {
    title = name;
    subTitle = email || phone || access_code || subTitleFallback;
  } else if (email) {
    title = email;
    subTitle = phone || access_code || subTitleFallback;
  } else if (phone) {
    title = phone;
    subTitle = access_code || subTitleFallback;
  } else if (access_code) {
    title = access_code;
    subTitle = subTitleFallback;
  } else {
    title = titleFallback;
  }

  let imageUrl;
  if (cover_moment_id) {
    imageUrl = MOMENT_IMAGE_URL(cover_moment_id);
  } else {
    imageUrl = '/img/timeline/default-user.png';
  }

  let createdAt = getLocalizedDate('en-US', created_at);

  // todo: put in shared spot
  let statusLabel = null;
  switch (status_id) {
    case 0:
      statusLabel = 'Not Invited';
      break;
    case 1:
      statusLabel = 'Preparing Invite';
      break;
    case 2:
      statusLabel = 'Invite Sent';
      break;
    case 3:
      statusLabel = 'Invite Viewed';
      break;
    case 4:
      statusLabel = 'Invite Failed';
  }

  const extraPanel = (
    <div>
      <p className={classes.extraPanelLine}>Created {createdAt}</p>
      <p className={classes.extraPanelLine}>{photo_count} Moments</p>
      <p className={classes.extraPanelLine}>{statusLabel}</p>
    </div>
  )

  const onClick = () => viewCustomer(id);
  const newTabLink = viewCustomer(id, true);

  let height = '100px';

  return buildResultItem({
    title,
    subTitle,
    imageUrl,
    extraPanel,
    onClick,
    newTabLink,
    height
  });
}

const buildJobItem = props => {
  const {
    query,
    result,
    hasProviders,
    indented,
    viewJob
  } = props;

  const {
    id,
    name,
    event_id,
    created_at,
    customer_count,
    moment_count
  } = result.job;

  let title = name;
  let subTitle = event_id;

  let imageUrl;
  if (hasProviders) {
    imageUrl = result.provider.logo_url;
  } else {
    imageUrl = result.lab.logo_url;
  }

  let createdAt = getLocalizedDate('en-US', created_at);

  const extraPanel = (
    <div>
      <p className={classes.extraPanelLine}>Created {createdAt}</p>
      <p className={classes.extraPanelLine}>{customer_count} Customers</p>
      <p className={classes.extraPanelLine}>{moment_count} Moments</p>
    </div>
  )

  const onClick = () => viewJob(id);
  const newTabLink = viewJob(id, true);

  let height = '100px';

  return buildResultItem({
    title,
    subTitle,
    imageUrl,
    extraPanel,
    onClick,
    newTabLink,
    indented,
    height
  });
}

const buildNoResultsItem = props => {
  const {
    query
  } = props;

  let text = <p>No results for <span className={classes.highlighted}>{query}</span>.</p>;

  return {
    text
  };
}

const buildLoadingItem = props => {
  const {
    query
  } = props;

  let text = <p>Loading results for <span className={classes.highlighted}>{query}</span>...</p>;

  let icon = <FontAwesomeIcon icon={faCircleNotch} spin />;

  return {
    text,
    icon
  };
}

const buildMenuItems = props => {
  const {
    query,
    loading,
    results,
    hasProviders,
    viewJob,
    viewJobs,
    viewCustomer,
    viewCustomers
  } = props;

  if (loading) {
    // implement loading screen
    return [
      buildHeaderItem({ header: 'Loading' }),
      buildLoadingItem(query)
    ];
  }

  // list customers first, and then jobs.
  // each customer will also have an indented job menu item under it.
  // each section will also have a header menuItem, i.e. "Customers", "Jobs".
  // if no results, then display a no results message.
  // the query should always be highlighted in the menu item text

  // begin by sorting results
  let customers = [], jobs = [];
  for (let result of results) {
    switch (result.result_type) {
      case CUSTOMER_RESULT_TYPE:
        customers.push(result);
        break;

      case JOB_RESULT_TYPE:
        jobs.push(result);
    }
  }

  // list customers first, then jobs, or a "no results" message
  let items = [];
  if (customers.length) {
    let linkText = `See all (${customers.length})`;
    let onLinkClick = () => viewCustomers();

    let newTab = true;
    let linkHref = viewCustomers(newTab);

    items.push(buildHeaderItem({
      header: "Customers",
      linkText,
      linkHref,
      onLinkClick
    }));

    for (let result of customers) {
      items.push(buildCustomerItem({
        query,
        result,
        viewCustomer
      }));

      let indented = true;
      items.push(buildJobItem({
        query,
        result,
        hasProviders,
        indented,
        viewJob
      }));
    }
  }
  if (jobs.length) {
    let linkText = `See all (${jobs.length})`;
    let onLinkClick = () => viewJobs();

    let newTab = true;
    let linkHref = viewJobs(newTab);

    items.push(buildHeaderItem({
      header: "Jobs",
      linkText,
      linkHref,
      onLinkClick
    }));

    for (let result of jobs) {
      items.push(buildJobItem({
        query,
        result,
        hasProviders,
        viewJob
      }));
    }
  }
  if (!items.length) {
    items.push(buildHeaderItem({
      header: "No Results"
    }));

    items.push(buildNoResultsItem({
      query
    }));
  }

  return items;
}

const ItemText = props => {
  const {
    title,
    subTitle
  } = props;

  return (
    <div>
      <p className={classes.itemTitle}>{title}</p>
      <p className={classes.itemSubTitle}>{subTitle}</p>
    </div>
  )
}

const ItemIcon = props => {
  const {
    imageUrl
  } = props;

  return <Avatar variant="square" src={imageUrl} />;
}

const MenuItem2 = props => {
  const {
    text,
    icon,
    actionIcon,
    onClick,
    itemHeight,
    divider,
    indented,
    style,
    extraPanel
  } = props;

  const sx = {
    height: `${itemHeight}px`,
    cursor: onClick ? 'pointer' : 'default',
    padding: '1rem',
    ...style
  }

  const menuItem = (
    <MenuItem sx={sx} onClick={onClick}>
      {indented && <ArrowRightIcon />}
      {icon && <ListItemIcon>{icon}</ListItemIcon>}
      <ListItemText>{text}</ListItemText>
      {actionIcon && <Typography variant="body2" sx={{ color: 'text.secondary' }}>{actionIcon}</Typography>}
      {extraPanel && <div>{extraPanel}</div>}
    </MenuItem>
  )

  if (divider) {
    return (
      <div>
        {menuItem}
        <Divider />
      </div>
    )
  } else {
    return menuItem;
  }
}

const GlobalSearchModal = props => {
  const {
    isOpen,
    onClose,
    drawerWidth
  } = props;

  const isMobile = useSelector(state => state.viewportReducer.isMobile);

  // if the account is a lab, then show the provider's logo for each job.
  // otherwise if it's a provider, then show the lab's logo
  const account = useSelector(state => state.portalReducer.account);
  const hasProviders = account.has_providers;

  const queryRef = useRef();

  const [state, setState] = useState({
    loading: false,
    results: []
  });

  const deeplink = useDeeplink();

  const onChange = debounce(async event => {
    queryRef.current = event.target.value;

    // only search if the input has a value
    if (queryRef.current) {
      setState(state => ({ ...state, loading: true }));

      let results = await globalSearch(account.id, queryRef.current) || [];

      setState(state => ({ ...state, results, loading: false }));
    }
  }, 1000);

  const viewJob = (id, newTab = false) => {
    // onClick handler for job result.
    // set Job grid filters in Redux and view grid
    let filterModel = {
      id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: id.toString(),
        operator: AND_OPERATOR,
      }]
    }

    if (newTab) {
      // return link to set as anchor href with target="_blank"
      return deeplink.getLink(jobs, filterModel);
    } else {
      deeplink.load(jobs, filterModel)
        .then(onClose);
    }
  }

  const viewJobs = (newTab = false) => {
    // onClick handler for "See all" link in header.
    // set Job grid filters in Redux and view grid
    let filterModel = {};

    // set column filter operator to OR
    filterModel[OPERATOR_KEY] = [{ value: OR_OPERATOR }];

    // get matched fields from results.
    // separate out customer_id fields,
    // which will include the ID and require an equals filterType
    let customerIdFields = [];
    let otherFields = [];

    for (let result of state.results) {
      for (let field of result.matched_fields) {
        if (field.match(/^customer_id:/)) {
          customerIdFields.push(field);
        } else {
          otherFields.push(field);
        }
      }
    }

    // build the filterModel, with customer IDs consolidated under one customer_id field
    if (customerIdFields.length) {
      filterModel['customer_id'] = [];
    }
    for (let field of customerIdFields) {
      let customerId = field.split(':')[1];

      filterModel['customer_id'].push({
        filterClass: 'text',
        filterType: 'equals',
        value: customerId,
        operator: OR_OPERATOR
      });
    }

    for (let field of otherFields) {
      filterModel[field] ||= [{
        filterClass: 'text',
        filterType: 'contains',
        value: queryRef.current,
        operator: OR_OPERATOR
      }]
    }

    if (newTab) {
      // return link to set as anchor href with target="_blank"
      return deeplink.getLink(jobs, filterModel);
    } else {
      deeplink.load(jobs, filterModel)
        .then(onClose);
    }
  }

  const viewCustomer = (id, newTab = false) => {
    // onClick handler for customer result.
    // set Customer grid filters in Redux and view grid
    let filterModel = {
      id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: id.toString(),
        operator: AND_OPERATOR,
      }]
    }

    if (newTab) {
      // return link to set as anchor href with target="_blank"
      return deeplink.getLink(customers, filterModel);
    } else {
      deeplink.load(customers, filterModel)
        .then(onClose);
    }
  }

  const viewCustomers = (newTab = false) => {
    // onClick handler for "See all" link in header.
    // set Customer grid filters in Redux and view grid
    let filterModel = {};

    // set column filter operator to OR
    filterModel[OPERATOR_KEY] = [{ value: OR_OPERATOR }];

    // get matched fields from results and build filterModel
    for (let result of state.results) {
      for (let field of result.matched_fields) {
        filterModel[field] ||= [{
          filterClass: 'text',
          filterType: 'contains',
          value: queryRef.current,
          operator: OR_OPERATOR
        }]
      }
    }

    if (newTab) {
      // return link to set as anchor href with target="_blank"
      return deeplink.getLink(customers, filterModel);
    } else {
      deeplink.load(customers, filterModel)
        .then(onClose);
    }
  }

  const title = 'Global Search';
  const subTitle = 'Search across account';

  let menuItems = buildMenuItems({
    query: queryRef.current,
    loading: state.loading,
    results: state.results,
    hasProviders,
    viewJob,
    viewJobs,
    viewCustomer,
    viewCustomers
  });

  // align form modal to right of screen on desktop
  let modalStyle = {};
  if (!isMobile) {
    modalStyle.justifyContent = 'end';
    modalStyle.marginRight = '1.5rem';
  }

  const paperStyle = {
    top: '2rem'
  }

  const paperSx = {
    width: '300px'
  }

  // keepMounted was introduced here to help with performance,
  // but it doesn't appear to help at all and is causing issues.
  // the performance issues in the job modal were otherwise addressed.
  // keeping this here in case we change our mind but disabling for now
  //let keepMounted = !unmountOnClose;
  let keepMounted = false;

  let output;
  if (state.loading) {
    output = <FontAwesomeIcon icon={faCircleNotch} spin />;
  } else if (!state.results.length) {
    output = <p>No results.</p>;
  } else {
    let itemHeight = props.itemHeight || 55;

    output = (
      <MenuList>
        {menuItems.map((item, i) => (
          <MenuItem2
            {...item}
            key={`menuitem-${i}`}
            itemHeight={item.height || itemHeight}
          />
        ))}
      </MenuList>
    );
  }

  return (
    <Modal {...{
      open: isOpen,
      onClose,
      title,
      subTitle,
      modalStyle,
      paperStyle,
      keepMounted
    }}>
      <TextField
        variant="outlined"
        sx={{ width: '100%' }}
        placeholder="Name, email, phone, access code, etc."
        onChange={onChange}
      />
      <Divider sx={{ margin: '0.5rem 0' }} />
      {output}
    </Modal>
  );
}

export default GlobalSearchModal;
